import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

/**
 * Reads `?auth_error=` from the URL and shows a friendly toast. Clears the
 * param from the URL so a page refresh does not re-show the toast.
 *
 * Used by auth flows that redirect on error (OAuth callback storage failure,
 * auth-return passwordless storage failure) to surface a user-visible message
 * after the redirect lands, since the redirecting code has no UI of its own.
 *
 * Mount this inside any top-level provider that has both an i18n provider and
 * a `sonner` Toaster in scope. Does not render any markup.
 */
export function AuthErrorToast() {
  const { t, ready } = useTranslation('errors');

  useEffect(() => {
    // Wait for the 'errors' namespace to finish loading before showing the
    // toast, otherwise t() returns the raw key and the user sees the
    // translation key literal.
    if (!ready) return;

    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (!authError) return;

    if (authError === 'storage_write_failed') {
      toast.error(t('AUTH_STORAGE_WRITE_FAILED_TITLE'), {
        description: t('AUTH_STORAGE_WRITE_FAILED_MESSAGE'),
      });
      // Telemetry: pairs with the token_write_readback_mismatch event so we
      // can count how many users actually saw the toast vs how many closed
      // the tab before the redirect landed.
      Sentry.addBreadcrumb({
        category: 'user-error-shown',
        message: 'AUTH_STORAGE_WRITE_FAILED',
        level: 'warning',
        data: { surface: 'toast', trigger: 'auth_error_query_param' },
      });
    }

    // Clean the param from the URL so refresh does not retrigger the toast.
    params.delete('auth_error');
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }, [t, ready]);

  return null;
}

export default AuthErrorToast;
